import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Production'da kendi origin'ini kullan, development'ta env'den al
const getApiUrl = () => {
  // Eğer production'daysa (preview değilse) kendi origin'ini kullan
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Preview ortamında değilsek, kendi origin'imizi kullan
    if (!hostname.includes('preview.emergentagent.com')) {
      return window.location.origin;
    }
  }
  // Development veya preview'da env'den al
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_URL = getApiUrl();

// Axios response interceptor for better error handling
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying, could be token issue
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      // Token expired or invalid - let the auth context handle it
      console.warn('[AUTH] Token may be expired or invalid');
    }
    
    // Network error - don't logout, just show error
    if (!error.response) {
      console.error('[NETWORK] Connection error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      // Only logout on specific auth errors, not network errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      } else {
        // Network error - don't logout, user might still be valid
        console.warn('[AUTH] Network error, keeping session');
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    const perms = user.permissions || [];
    return perms.includes('all') || perms.includes(permission);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    hasPermission,
    isAuthenticated: !!user,
    isAdmin: user?.permissions?.includes('all'),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
